import { useState, useEffect, useMemo, useCallback } from 'react';
import AccountingConnectionsView from './AccountingConnectionsView';
import PayoutRoutingSettingsPanel from './PayoutRoutingSettingsPanel';
import { saveToast } from '../ui/SaveToast';

function FinancialsSettingsView() {
    const [activeTab, setActiveTab] = useState('overview');
    const [showPlaidConnect, setShowPlaidConnect] = useState(false);
    
    // Sample data for Chart of Accounts
    const [chartOfAccounts, setChartOfAccounts] = useState([
        { id: 1, code: '1000', name: 'Cash', type: 'Asset', balance: 25000 },
        { id: 2, code: '1100', name: 'Accounts Receivable', type: 'Asset', balance: 15000 },
        { id: 3, code: '1200', name: 'Inventory', type: 'Asset', balance: 8000 },
        { id: 4, code: '2000', name: 'Accounts Payable', type: 'Liability', balance: 5000 },
        { id: 5, code: '3000', name: 'Owner\'s Equity', type: 'Equity', balance: 50000 },
        { id: 6, code: '4000', name: 'Sales Revenue', type: 'Income', balance: 75000 },
        { id: 7, code: '5000', name: 'Cost of Goods Sold', type: 'Expense', balance: 30000 },
        { id: 8, code: '6000', name: 'Operating Expenses', type: 'Expense', balance: 20000 }
    ]);
    
    // Sample connected banks
    const [connectedBanks, setConnectedBanks] = useState([
        { id: 1, name: 'Chase Business Checking', last4: '8234', balance: 45230.50, connected: '2024-01-15' },
        { id: 2, name: 'Wells Fargo Savings', last4: '9912', balance: 12500.00, connected: '2024-02-20' }
    ]);
    
    // Sample payment methods
    const [paymentMethods, setPaymentMethods] = useState([
        { id: 1, type: 'Stripe', status: 'Active', transactionFee: '2.9% + $0.30' },
        { id: 2, type: 'PayPal', status: 'Active', transactionFee: '3.49% + $0.49' },
        { id: 3, type: 'Square', status: 'Inactive', transactionFee: '2.6% + $0.10' }
    ]);
    
    const FinancialsTile = ({ icon, title, description, color, count, onClick }) => (
        <div onClick={onClick} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all cursor-pointer group">
            <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center text-3xl flex-shrink-0 ${color}`}>
                    {icon}
                </div>
                <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-[var(--accent-ink-hover)] transition text-lg">{title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{description}</p>
                    {count !== undefined && (
                        <div className="text-2xl font-bold text-gray-900">{count}</div>
                    )}
                </div>
                <div className="text-gray-400 text-2xl">›</div>
            </div>
        </div>
    );
    
    return (
        <div className="ia-settings-financials max-w-7xl mx-auto">
            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-6 py-4 font-medium transition ${
                            activeTab === 'overview'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('chart-of-accounts')}
                        className={`px-6 py-4 font-medium transition ${
                            activeTab === 'chart-of-accounts'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Chart of Accounts
                    </button>
                    <button
                        onClick={() => setActiveTab('banking')}
                        className={`px-6 py-4 font-medium transition ${
                            activeTab === 'banking'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Banking
                    </button>
                    <button
                        onClick={() => setActiveTab('billing')}
                        className={`px-6 py-4 font-medium transition ${
                            activeTab === 'billing'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Billing & Payments
                    </button>
                    <button
                        onClick={() => setActiveTab('targets')}
                        className={`px-6 py-4 font-medium transition ${
                            activeTab === 'targets'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Targets & Goals
                    </button>
                    <button
                        onClick={() => setActiveTab('accounting')}
                        className={`px-6 py-4 font-medium transition ${
                            activeTab === 'accounting'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Accounting
                    </button>
                    <button
                        onClick={() => setActiveTab('routing')}
                        className={`px-6 py-4 font-medium transition ${
                            activeTab === 'routing'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Tax & Routing
                    </button>
                </div>
            </div>
            
            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <FinancialsTile
                        icon="📊"
                        title="Chart of Accounts"
                        description="Manage your accounting categories and GL codes"
                        color="bg-blue-100"
                        count={chartOfAccounts.length}
                        onClick={() => setActiveTab('chart-of-accounts')}
                    />
                    <FinancialsTile
                        icon="🏦"
                        title="Banking (Plaid)"
                        description="Connect and manage your bank accounts"
                        color="bg-green-100"
                        count={connectedBanks.length}
                        onClick={() => setActiveTab('banking')}
                    />
                    <FinancialsTile
                        icon="💳"
                        title="Billing & Payments"
                        description="Configure payment methods and billing settings"
                        color="bg-purple-100"
                        count={paymentMethods.length}
                        onClick={() => setActiveTab('billing')}
                    />
                    <FinancialsTile
                        icon="🎯"
                        title="Targets & Goals"
                        description="Set financial targets and business goals"
                        color="bg-[var(--viz-orange)]/10"
                        onClick={() => setActiveTab('targets')}
                    />
                    <FinancialsTile
                        icon="🔗"
                        title="Accounting Connections"
                        description="Connect QuickBooks Online or Xero for two-way sync"
                        color="bg-indigo-100"
                        onClick={() => setActiveTab('accounting')}
                    />
                </div>
            )}
            
            {/* Chart of Accounts Tab */}
            {activeTab === 'chart-of-accounts' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Chart of Accounts</h3>
                                    <p className="text-sm text-gray-600 mt-1">Manage your accounting categories and general ledger codes</p>
                                </div>
                                <button className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium">
                                    + Add Account
                                </button>
                            </div>
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {chartOfAccounts.map(account => (
                                        <tr key={account.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{account.code}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{account.name}</td>
                                            <td className="px-6 py-4 text-sm">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    account.type === 'Asset' ? 'bg-blue-100 text-blue-800' :
                                                    account.type === 'Liability' ? 'bg-red-100 text-red-800' :
                                                    account.type === 'Equity' ? 'bg-purple-100 text-purple-800' :
                                                    account.type === 'Income' ? 'bg-green-100 text-green-800' :
                                                    'bg-[var(--viz-orange)]/10 text-[var(--viz-orange)]'
                                                }`}>
                                                    {account.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                                                ${account.balance.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-center">
                                                <button className="text-[var(--accent-ink)] hover:text-[var(--accent-ink-hover)] font-medium mr-3">Edit</button>
                                                <button className="text-red-600 hover:text-red-700 font-medium">Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Banking Tab */}
            {activeTab === 'banking' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Connected Bank Accounts</h3>
                                    <p className="text-sm text-gray-600 mt-1">Securely connect your bank accounts via Plaid</p>
                                </div>
                                <button 
                                    onClick={() => setShowPlaidConnect(true)}
                                    className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium"
                                >
                                    + Connect Bank Account
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            {connectedBanks.map(bank => (
                                <div key={bank.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-2xl">
                                                🏦
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">{bank.name}</h4>
                                                <p className="text-sm text-gray-600">****{bank.last4}</p>
                                                <p className="text-xs text-gray-500 mt-1">Connected on {bank.connected}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-bold text-gray-900">${bank.balance.toLocaleString()}</div>
                                            <p className="text-xs text-gray-500">Current Balance</p>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex gap-2">
                                        <button className="px-3 py-1 text-sm text-[var(--accent-ink)] hover:text-[var(--accent-ink-hover)] font-medium">Sync Now</button>
                                        <button className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700 font-medium">View Transactions</button>
                                        <button className="px-3 py-1 text-sm text-red-600 hover:text-red-700 font-medium">Disconnect</button>
                                    </div>
                                </div>
                            ))}
                            
                            {connectedBanks.length === 0 && (
                                <div className="text-center py-12">
                                    <div className="text-6xl mb-4">🏦</div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Banks Connected</h3>
                                    <p className="text-gray-600 mb-6">Connect your bank accounts to automatically sync transactions</p>
                                    <button 
                                        onClick={() => setShowPlaidConnect(true)}
                                        className="px-6 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium"
                                    >
                                        Connect Your First Bank Account
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Plaid Info Card */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                            <div className="text-3xl">ℹ️</div>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">About Plaid Integration</h4>
                                <p className="text-sm text-gray-700 mb-2">
                                    Plaid is a secure service that connects your bank accounts to IntelAgent. Your credentials are never stored on our servers.
                                </p>
                                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                                    <li>Bank-level security and encryption</li>
                                    <li>Read-only access to transaction data</li>
                                    <li>Automatic daily synchronization</li>
                                    <li>Support for 10,000+ financial institutions</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Billing & Payments Tab */}
            {activeTab === 'billing' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Payment Methods</h3>
                                    <p className="text-sm text-gray-600 mt-1">Configure how you accept payments from clients</p>
                                </div>
                                <button className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium">
                                    + Add Payment Method
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            {paymentMethods.map(method => (
                                <div key={method.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${
                                                method.type === 'Stripe' ? 'bg-purple-100' :
                                                method.type === 'PayPal' ? 'bg-blue-100' :
                                                'bg-gray-100'
                                            }`}>
                                                {method.type === 'Stripe' ? '💳' : method.type === 'PayPal' ? '🅿️' : '⬛'}
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">{method.type}</h4>
                                                <p className="text-sm text-gray-600">Transaction Fee: {method.transactionFee}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                method.status === 'Active' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {method.status}
                                            </span>
                                            <button className="text-[var(--accent-ink)] hover:text-[var(--accent-ink-hover)] font-medium text-sm">
                                                {method.status === 'Active' ? 'Configure' : 'Activate'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Billing Settings */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">Billing Settings</h3>
                            <p className="text-sm text-gray-600 mt-1">Configure invoicing and billing preferences</p>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h4 className="font-medium text-gray-900">Default Payment Terms</h4>
                                    <p className="text-sm text-gray-600">How long clients have to pay invoices</p>
                                </div>
                                <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]">
                                    <option>Net 30</option>
                                    <option>Net 15</option>
                                    <option>Net 60</option>
                                    <option>Due on Receipt</option>
                                </select>
                            </div>
                            
                            <div className="flex justify-between items-center">
                                <div>
                                    <h4 className="font-medium text-gray-900">Late Payment Fee</h4>
                                    <p className="text-sm text-gray-600">Charge for overdue invoices</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input type="number" placeholder="5" className="w-20 px-3 py-2 border border-gray-300 rounded-lg" />
                                    <span className="text-gray-700">%</span>
                                </div>
                            </div>
                            
                            <div className="flex justify-between items-center">
                                <div>
                                    <h4 className="font-medium text-gray-900">Automatic Reminders</h4>
                                    <p className="text-sm text-gray-600">Send reminders for unpaid invoices</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" className="sr-only peer" defaultChecked />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--accent-ink)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--btn-fill)]"></div>
                                </label>
                            </div>
                            
                            <div className="flex justify-between items-center">
                                <div>
                                    <h4 className="font-medium text-gray-900">Accept Partial Payments</h4>
                                    <p className="text-sm text-gray-600">Allow clients to pay invoices in installments</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" className="sr-only peer" />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--accent-ink)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--btn-fill)]"></div>
                                </label>
                            </div>
                            
                            <div className="pt-4 border-t border-gray-200">
                                <button className="px-6 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium">
                                    Save Billing Settings
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Targets & Goals Tab - Phase 4.62 */}
            {activeTab === 'targets' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">Financial Targets</h3>
                            <p className="text-sm text-gray-600 mt-1">Set and track your business financial goals</p>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            {/* Monthly Income Target - Phase 4.62 */}
                            <div className="border border-gray-200 rounded-lg p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-900">Monthly Income Target</h4>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Set your monthly income goal. The dashboard Handle Rate meter will track progress toward this target.
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Target Amount
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2.5 text-gray-500 text-lg">$</span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="999999"
                                                step="0.01"
                                                defaultValue={parseFloat(localStorage.getItem('monthlyIncomeTarget') || '0')}
                                                onChange={(e) => {
                                                    const value = parseFloat(e.target.value) || 0;
                                                    const clamped = Math.min(Math.max(value, 0), 999999.99);
                                                    localStorage.setItem('monthlyIncomeTarget', clamped.toString());
                                                }}
                                                placeholder="0.00"
                                                className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)] focus:border-transparent text-lg"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Range: $0.00 - $999,999.99
                                        </p>
                                    </div>
                                    
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <div className="flex gap-3">
                                            <div className="text-blue-600 text-xl">ℹ️</div>
                                            <div className="flex-1">
                                                <h5 className="font-semibold text-blue-900 mb-1">How This Works</h5>
                                                <p className="text-sm text-blue-800">
                                                    The "MONTHLY INCOME TARGET" meter on your dashboard will show:
                                                </p>
                                                <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                                                    <li>Current month's income as a percentage of your target</li>
                                                    <li>Comparison to previous month's performance</li>
                                                    <li>Progress indicator to help you track toward your goal</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="pt-4 border-t border-gray-200">
                                        <button 
                                            onClick={() => {
                                                // The target is already stored as it is typed and the dashboard reads it when it
                                                // opens, so there is nothing to reload: just confirm.
                                                saveToast.saved();
                                            }}
                                            className="px-6 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium"
                                        >
                                            Save Target
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Future Targets - Placeholder */}
                            <div className="border border-gray-200 rounded-lg p-6 opacity-60">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="text-2xl">📈</div>
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-900">Quarterly Revenue Target</h4>
                                        <p className="text-sm text-gray-600">Coming Soon</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="border border-gray-200 rounded-lg p-6 opacity-60">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="text-2xl">💰</div>
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-900">Annual Profit Target</h4>
                                        <p className="text-sm text-gray-600">Coming Soon</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Plaid Connect Modal */}
            {showPlaidConnect && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6">
                            <h3 className="text-2xl font-bold mb-4">Connect Bank Account</h3>
                            <div className="text-center py-8">
                                <div className="text-6xl mb-4">🏦</div>
                                <h4 className="text-xl font-semibold text-gray-900 mb-2">Plaid Integration</h4>
                                <p className="text-gray-600 mb-6">
                                    This feature requires Plaid Link to be implemented. In production, this would open the secure Plaid authentication flow.
                                </p>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                                    <h5 className="font-semibold text-gray-900 mb-2">Implementation Steps:</h5>
                                    <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                                        <li>Sign up for Plaid developer account</li>
                                        <li>Get your Client ID and Secret</li>
                                        <li>Add Plaid Link script to your app</li>
                                        <li>Implement backend webhook handlers</li>
                                        <li>Store access tokens securely</li>
                                    </ol>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3">
                                <button 
                                    onClick={() => setShowPlaidConnect(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Close
                                </button>
                                <button className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)]">
                                    Learn More About Plaid
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Accounting Connections Tab — BL-180 Gate 1 */}
            {activeTab === 'accounting' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <AccountingConnectionsView />
                </div>
            )}

            {activeTab === 'routing' && (
                <PayoutRoutingSettingsPanel />
            )}
        </div>
    );
}


export default FinancialsSettingsView;
